import { useEffect, useState } from 'react';
import {
  apiClient,
  type ProjectSummary,
  type MeResponse,
  ApiError,
} from '@/lib/api/client';
import { ProjectAvatar } from '@/components/ProjectAvatar';
import { RadialOrbital, type OrbitalItem } from './RadialOrbital';
import { cn } from '@/lib/utils';

type ViewMode = 'orbital' | 'grid';
const VIEW_PREF_KEY = 'apetech-pm:projects-view';

interface Props {
  /** Bumping this OR changing activeWsId re-fetches the project list. */
  refreshKey: number;
  /** Tracked in deps so a workspace switch (which sets the apm_ws cookie server-side
   * before the next /v1/projects call) re-runs the query against the new active ws. */
  activeWsId: string | null;
  /** Passed in so the orbital center bubble can render the workspace identity. */
  activeWorkspace: MeResponse['workspaces'][number] | null;
  onOpenProject: (project_id: string) => void;
  onNewProject: () => void;
}

export function ProjectsList({
  refreshKey,
  activeWsId,
  activeWorkspace,
  onOpenProject,
  onNewProject,
}: Props) {
  const [projects, setProjects] = useState<ProjectSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>(() => {
    try {
      const v = localStorage.getItem(VIEW_PREF_KEY);
      return v === 'grid' ? 'grid' : 'orbital';
    } catch {
      return 'orbital';
    }
  });
  function setViewPersist(v: ViewMode) {
    setView(v);
    try {
      localStorage.setItem(VIEW_PREF_KEY, v);
    } catch {/* ignore */}
  }
  const SHOW_CLOSED_KEY = 'apetech-pm:projects-show-closed';
  const [showClosed, setShowClosed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SHOW_CLOSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  function setShowClosedPersist(v: boolean) {
    setShowClosed(v);
    try {
      localStorage.setItem(SHOW_CLOSED_KEY, v ? '1' : '0');
    } catch {/* ignore */}
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.listProjects();
        if (!cancelled) {
          setProjects(r.projects);
          setErr(null);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Failed to load');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey, activeWsId]);

  if (err) {
    return (
      <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm">
        <span>{err}</span>
        <button
          type="button"
          onClick={() => {
            setErr(null);
            // re-trigger the same useEffect by bumping refreshKey upstream via
            // window reload as a last-ditch — but a cheap retry is to just
            // re-fetch directly here.
            apiClient
              .listProjects()
              .then((r) => setProjects(r.projects))
              .catch((e) => setErr(e instanceof ApiError ? e.message : 'Failed to load'));
          }}
          className="rounded-md border border-border bg-background px-3 py-1 text-xs font-medium text-foreground hover:border-ring/60"
        >
          Retry
        </button>
      </div>
    );
  }
  if (projects === null) {
    return (
      <div
        className="grid gap-3 md:grid-cols-2 lg:grid-cols-3"
        aria-busy="true"
        aria-label="Loading projects"
      >
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-xl bg-card" />
        ))}
      </div>
    );
  }

  // Hide closed projects from orbital + grid by default. Count separately
  // so the toggle can show "+ N closed" when there are any to surface.
  const visible = showClosed ? projects : projects.filter((p) => !p.closed_at);
  const closedCount = projects.filter((p) => !!p.closed_at).length;
  const home = visible.filter((p) => p.home === 1);
  const pinned = visible.filter((p) => p.home === 0);
  const all = [...home, ...pinned];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            Projects in this workspace
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground/70">
            {home.length + pinned.length} {home.length + pinned.length === 1 ? 'project' : 'projects'}
            {pinned.length > 0 && ` · ${pinned.length} pinned from other workspaces`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {closedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowClosedPersist(!showClosed)}
              aria-pressed={showClosed}
              className={cn(
                'rounded-md border px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest transition-colors',
                showClosed
                  ? 'border-primary bg-primary/15 text-primary'
                  : 'border-border text-muted-foreground hover:border-ring/60 hover:text-foreground',
              )}
              title={showClosed ? 'Hide closed projects' : `Show ${closedCount} closed project${closedCount === 1 ? '' : 's'}`}
            >
              {showClosed ? `Hide closed (${closedCount})` : `Show closed (${closedCount})`}
            </button>
          )}
          <ViewToggle view={view} onChange={setViewPersist} />
          <button
            onClick={onNewProject}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.98]"
          >
            New project
          </button>
        </div>
      </div>

      {view === 'orbital' ? (
        <RadialOrbital
          center={
            activeWorkspace
              ? {
                  kind: 'workspace',
                  name: activeWorkspace.name,
                  image_key: activeWorkspace.image_key,
                  emoji: activeWorkspace.emoji,
                  color: null,
                }
              : { kind: 'all', userInitial: '?' }
          }
          items={all.map(toOrbitalItem)}
          onOpenProject={onOpenProject}
          emptyCta={
            <button
              onClick={onNewProject}
              className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 active:scale-[0.98]"
            >
              New project
            </button>
          }
        />
      ) : (
        <div className="space-y-8">
          <Section title="In this workspace">
            {home.length === 0 ? (
              <Empty label="No projects yet" cta="Click New project to start one." />
            ) : (
              <Grid items={home} onOpen={onOpenProject} />
            )}
          </Section>
          {pinned.length > 0 && (
            <Section title="Pinned here" subtitle="Projects shared with you, filed under this workspace.">
              <Grid items={pinned} onOpen={onOpenProject} />
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function toOrbitalItem(p: ProjectSummary): OrbitalItem {
  return {
    id: p.project_id,
    name: p.name,
    slug: p.slug,
    role: p.role,
    color: p.color,
    image_key: p.image_key,
    emoji: p.emoji,
    task_open: p.task_open,
    task_done: p.task_done,
    task_total: p.task_total,
    members_preview: p.members_preview,
    member_count: p.member_count,
    created_at: p.created_at,
  };
}

function ViewToggle({ view, onChange }: { view: ViewMode; onChange: (v: ViewMode) => void }) {
  return (
    <div
      className="inline-flex items-center gap-px rounded-full border border-border bg-card p-0.5"
      role="group"
      aria-label="View mode"
    >
      {(['orbital', 'grid'] as const).map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          aria-pressed={view === v}
          className={cn(
            'rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors duration-150',
            view === v
              ? 'bg-secondary text-secondary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function Section({
  title,
  subtitle,
  cta,
  children,
}: {
  title: string;
  subtitle?: string;
  cta?: { label: string; onClick: () => void };
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {title}
          </div>
          {subtitle && <div className="text-xs text-muted-foreground/80">{subtitle}</div>}
        </div>
        {cta && (
          <button
            onClick={cta.onClick}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 active:scale-[0.98]"
          >
            {cta.label}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ label, cta }: { label: string; cta: string }) {
  return (
    <div className="rounded-xl border border-dashed border-border bg-card/40 px-5 py-10 text-center">
      <div className="text-sm text-foreground">{label}</div>
      <div className="mt-1 text-xs text-muted-foreground">{cta}</div>
    </div>
  );
}

/**
 * Project card: avatar (image → emoji → letter), name, task progress bar,
 * member-avatar stack. Designed for scan-first, click-to-drill flow per the
 * recognition-over-recall principle.
 */
function ProjectCard({ project, onOpen }: { project: ProjectSummary; onOpen: () => void }) {
  const p = project;
  const taskPct = p.task_total > 0 ? Math.round((p.task_done / p.task_total) * 100) : 0;
  return (
    <button
      onClick={onOpen}
      className="group flex flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left transition-colors duration-150 hover:border-ring/60 hover:bg-card/80"
    >
      <div className="flex items-start gap-3">
        <ProjectAvatar
          name={p.name}
          image_key={p.image_key}
          emoji={p.emoji}
          color={p.color}
          size="md"
          shape="rounded"
        />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
            {p.name}
          </div>
          <div className="truncate font-mono text-[10px] text-muted-foreground">
            /p/{p.slug} · {p.role}
          </div>
        </div>
      </div>

      {/* Task progress */}
      <div className="space-y-1">
        <div className="flex items-baseline justify-between font-mono text-[10px] text-muted-foreground">
          <span>
            {p.task_open > 0 ? (
              <span>
                <span className="text-foreground">{p.task_open}</span> open
                {p.task_total > 0 && (
                  <span className="text-muted-foreground/70"> · {p.task_total} total</span>
                )}
              </span>
            ) : p.task_total > 0 ? (
              <span className="text-foreground">All {p.task_total} done</span>
            ) : (
              <span className="text-muted-foreground/70">No tasks yet</span>
            )}
          </span>
          {p.task_total > 0 && <span>{taskPct}%</span>}
        </div>
        <div
          className="h-1 overflow-hidden rounded-full bg-card-foreground/10"
          role="progressbar"
          aria-valuenow={taskPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${p.task_done} of ${p.task_total} tasks done`}
        >
          {/* Animate scaleX (compositor-only) instead of width (layout). */}
          <div
            className={cn(
              'h-full origin-left rounded-full transition-transform duration-500 will-change-transform',
              p.task_total > 0 ? 'bg-primary' : 'bg-card-foreground/0',
            )}
            style={{ width: '100%', transform: `scaleX(${taskPct / 100})` }}
          />
        </div>
      </div>

      {/* Member stack */}
      {p.member_count > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {p.members_preview.map((m) => (
              <span
                key={m.user_id}
                className="grid size-6 place-items-center rounded-full bg-secondary text-[10px] font-semibold text-secondary-foreground ring-2 ring-card"
                title={`${m.email} (${m.role})`}
                aria-label={`${m.email} (${m.role})`}
              >
                {m.email[0]?.toUpperCase()}
              </span>
            ))}
            {p.member_count > p.members_preview.length && (
              <span
                className="grid size-6 place-items-center rounded-full bg-card-foreground/10 text-[10px] font-mono text-muted-foreground ring-2 ring-card"
                aria-label={`+${p.member_count - p.members_preview.length} more members`}
              >
                +{p.member_count - p.members_preview.length}
              </span>
            )}
          </div>
          <span className="font-mono text-[10px] text-muted-foreground">
            {p.member_count} {p.member_count === 1 ? 'member' : 'members'}
          </span>
        </div>
      )}
    </button>
  );
}

function Grid({
  items,
  onOpen,
}: {
  items: ProjectSummary[];
  onOpen: (project_id: string) => void;
}) {
  return (
    <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
      {items.map((p) => (
        <ProjectCard key={p.project_id} project={p} onOpen={() => onOpen(p.project_id)} />
      ))}
    </div>
  );
}
